import { Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, FormLabel, TextField, Stack, InputAdornment, Typography, Grid, Divider, Box, Tab, Tabs, CircularProgress, LinearProgress } from "@mui/material";
import NumberSpinner from "./NumberSpinner";
import { useEffect, useState } from "react";
import { gray } from "../shared-theme/themePrimitives";
import { green, red } from "@mui/material/colors";
import { CreateStudent } from "../FirebaseQueries";
import { Cancel, CheckCircle } from "@mui/icons-material";
import { useAuth } from "../AuthProvider";

interface Props {
	isOpen: boolean;
	onClose: () => void;
}

interface Result {
	id: string,
	email: string,
	message: string,
	success: boolean
}

export default function BulkCreateStudentsDialog(props: Props) {
	const { isOpen, onClose } = props;
	const { resetPassword } = useAuth();

	const sequenceKey: string = "{n}";
	const [emailDomain, setEmailDomain] = useState("example.com");
	const [startNumber, setStartNumber] = useState(1);
	const [count, setCount] = useState(3);
	const [template, setTemplate] = useState(`student_${sequenceKey}`);

	const [emailDomainError, setEmailDomainError] = useState("");
	const [templateError, setTemplateError] = useState("");
	const redColour: string = red[600];

	const [tabIndex, setTabIndex] = useState(0);
	const [isCreating, setIsCreating] = useState(false);
	const [progress, setProgress] = useState(100);
	const [results, setResults] = useState<Result[]>([]);



	// ---------- Computed Values ----------

	function HasError(): boolean {
		const hasEmailDomainError = emailDomainError !== "";
		const hasTemplateError = templateError !== "";

		return hasEmailDomainError || hasTemplateError;
	}

	function GetPreviewEmails(template: string, sequenceKey: string, startNumber: number, count: number, emailDomain: string): string[] {
		let previewEmails: string[] = [];

		if (count < 8) {
			for (let i = startNumber; i < (count + startNumber); i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewEmails.push(`${filledTemplate}@${emailDomain}`);
			}
		} else {
			for (let i = startNumber; i < (startNumber + 3); i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewEmails.push(`${filledTemplate}@${emailDomain}`);
			}

			previewEmails.push("...");

			for (let i = (count + startNumber - 3); i < count + startNumber; i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewEmails.push(`${filledTemplate}@${emailDomain}`);
			}
		}

		return previewEmails;
	}

	function GetPreviewNames(template: string, sequenceKey: string, startNumber: number, count: number): string[] {
		let previewNames: string[] = [];

		if (count < 8) {
			for (let i = startNumber; i < (count + startNumber); i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewNames.push(filledTemplate);
			}
		} else {
			for (let i = startNumber; i < (startNumber + 3); i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewNames.push(filledTemplate);
			}

			previewNames.push("...");

			for (let i = (count + startNumber - 3); i < count + startNumber; i++) {
				let filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
				previewNames.push(filledTemplate);
			}
		}

		return previewNames;
	}

	function GetRandomPassword() {
		let passwordArray: string[] = [];
		const length = 6;

		const lowerCharsCount = randomInt(1, length - 2);
		const upperCharsCount = randomInt(1, length - lowerCharsCount - 1);
		const digitsCount = length - lowerCharsCount - upperCharsCount;

		const lowerChars = "abcdefghijklmnopqrstuvwxyz"
		const upperChars = lowerChars.toUpperCase();
		const digits = "0123456789";

		for (let i = 0; i < lowerCharsCount; i++) passwordArray.push(randomChar(lowerChars));
		for (let i = 0; i < upperCharsCount; i++) passwordArray.push(randomChar(upperChars));
		for (let i = 0; i < digitsCount; i++) passwordArray.push(randomChar(digits));

		passwordArray.sort(() => Math.random() - 0.5);
		return passwordArray.join("");
	}

	function randomInt(min: number, max: number) {
		return Math.floor(Math.random() * (max - min + 1)) + min;
	}

	function randomChar(str: string) {
		return str[Math.floor(Math.random() * str.length)];
	}

	// ---------- useEffect ----------

	useEffect(() => {
		if (isOpen) {
			setEmailDomain("example.com");
			setStartNumber(1);
			setCount(3);
			setTemplate(`student_${sequenceKey}`);

			setEmailDomainError("");
			setTemplateError("");

			setTabIndex(0);
		}
	}, [isOpen])

	// ---------- State Handlers ----------

	function HandleEmailDomainUpdate(event: any) {
		const value: string = event.target.value;
		setEmailDomainError(value === "" ? "Must be filled." : "");
		setEmailDomain(event.target.value);
		// onCodeChanged(event.target.value);
	}

	function HandleStartNumberUpdate(value: any) {
		if (value !== null) setStartNumber(value);
	}

	function HandleCountUpdate(value: any) {
		if (value !== null) setCount(value);
	}

	function HandleTemplateUpdate(event: any) {
		const value: string = event.target.value;
		if (!value.includes(sequenceKey)) {
			setTemplateError(`Template must include sequence key: ${sequenceKey}.`);
		}
		else {
			setTemplateError("");
		}
		setTemplate(value);
	}

	function HandleTabIndexChange(event: any) {
		const index: number = event.target.tabIndex;
		if (event !== null) setTabIndex(index);
	}

	// ---------- Component Functions ----------

	async function HandleOnCreate() {
		setTabIndex(1);
		setIsCreating(true);
		setProgress(0);

		let messages: any[] = [];

		for (let i = startNumber; i < (count + startNumber); i++) {
			const filledTemplate: string = template.replaceAll(sequenceKey, (i).toString());
			const accountEmail: string = `${filledTemplate}@${emailDomain}`;
			const accountName: string = filledTemplate;
			const password: string = GetRandomPassword();
			console.log(password);

			try {
				const res: Result = await CreateStudent(accountEmail, password, accountName);
				messages.push(res);
				if (res.success) resetPassword(accountEmail);
			} catch (err) {
				messages.push(err);
			}

			setProgress((messages.length / count) * 100)
		}

		setResults(messages);
		setIsCreating(false);
	}

	// async function OnDelete() {
	// 	let ids: string[] = [];

	// 	results.forEach(result => {
	// 		if (result.id !== "") ids.push(result.id);
	// 	});

	// 	if (ids.length > 0) {
	// 		try {
	// 			const res = await DeleteUsers(ids);
	// 			console.log(res);
	// 		} catch (err) {
	// 			console.log(err);
	// 		}
	// 	}
	// }

	return (
		<Dialog onClose={onClose} open={isOpen}>
			<DialogTitle>Bulk Create Students</DialogTitle>
			<DialogContent sx={{ minWidth: 500 }}>
				<Stack gap={2}>
					<Stack direction={"row"} gap={1}>
						{/* ---------- Start Number ---------- */}
						<FormControl>
							<FormLabel htmlFor="startNumber">Start Number</FormLabel>
							<NumberSpinner value={startNumber} onValueChange={HandleStartNumberUpdate} min={0} />
						</FormControl>
						{/* ---------- Count ---------- */}
						<FormControl>
							<FormLabel htmlFor="count">Count</FormLabel>
							<NumberSpinner value={count} onValueChange={HandleCountUpdate} min={1} />
						</FormControl>
						{/* ---------- Email Domain ---------- */}
						<FormControl>
							<FormLabel htmlFor="email">Email Domain</FormLabel>
							<TextField
								id="email"
								value={emailDomain}
								placeholder="example.com"
								variant="outlined"
								onChange={HandleEmailDomainUpdate}
							>
							</TextField>
							<span style={{ color: redColour, minHeight: 22 }}>{emailDomainError}</span>
						</FormControl>
					</Stack>
					{/* ---------- Template ---------- */}
					<FormControl>
						<FormLabel htmlFor="template">{"Template (Add {n} in place of sequence)"}</FormLabel>
						<TextField
							id="template"
							value={template}
							placeholder="{n}"
							variant="outlined"
							onChange={HandleTemplateUpdate}
							slotProps={{
								input: {
									endAdornment: <InputAdornment position="end">@{emailDomain}</InputAdornment>,
								},
							}}
						// error={codeErrorText !== ""}
						>
						</TextField>
						<span style={{ color: redColour, minHeight: 22 }}>{templateError}</span>
					</FormControl>

					<Stack gap={0.5}>
						<Tabs value={tabIndex} onChange={HandleTabIndexChange}>
							<Tab label="Preview" tabIndex={0} />
							<Tab label="Results" tabIndex={1} />
						</Tabs>
						<Box border={1} borderRadius={1} borderColor={gray[200]} padding={2} height={182} sx={{ overflowY: "auto" }}>
							{tabIndex === 0 ?
								<>
									{HasError() ?
										<Typography color={redColour}>Please resolve errors.</Typography>
										:
										<Grid container spacing={2}>
											<Grid size={6}>
												<Typography fontWeight={"bold"}>Email</Typography>
												<Divider sx={{ marginBottom: 1 }}></Divider>
												{GetPreviewEmails(template, sequenceKey, startNumber, count, emailDomain).map((email, index) => (
													<Typography fontFamily={'Roboto Mono Variable'} key={index}>{email}</Typography>
												))}
											</Grid>
											<Grid size={6}>
												<Typography fontWeight={"bold"}>Display Name</Typography>
												<Divider sx={{ marginBottom: 1 }}></Divider>
												{GetPreviewNames(template, sequenceKey, startNumber, count).map((name, index) => (
													<Typography fontFamily={'Roboto Mono Variable'} key={index}>{name}</Typography>
												))}
											</Grid>
										</Grid>
									}</>
								:
								<>
									{isCreating ?
										<Stack height={"100%"} justifyContent={"center"} paddingX={8}>
											<LinearProgress
												variant="determinate"
												value={progress}
												aria-label="Create Students"
											/>
										</Stack>
										:
										<>
											{results.length === 0 ?
												<Typography>No results yet.</Typography>
												:
												<Stack gap={0.5}>
													{results.sort((a, b) => Number(a.success) - Number(b.success)).map((value, index) => (
														<Stack
															key={index}
															direction={"row"}
															alignItems={"center"}
															gap={2}
															padding={1}
															borderRadius={1}
															bgcolor={value.success ? green[50] : red[50]}
														>
															{value.success ?
																<CheckCircle htmlColor={green[500]} />
																:
																<Cancel htmlColor={red[500]} />
															}
															<Typography flexGrow={1}>{value.email}</Typography>
															<Typography flexGrow={1}>{value.message}</Typography>
														</Stack>
													))}
												</Stack>
											}
										</>
									}
								</>
							}
						</Box>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				{/* <Button onClick={OnDelete} color="error" variant="contained">Delete</Button> */}
				<Button onClick={onClose}>Close</Button>
				<Button onClick={HandleOnCreate} variant="contained" disabled={HasError() || isCreating}>{HasError() ? "Error" : `Create ${count} accounts`}</Button>
			</DialogActions>
		</Dialog>
	);
}